import { useState, useContext, useEffect } from "react";
import "./Miscellaneous.css";
import { Radio } from "antd";

//API
import axios from "../../../service/axios";

//Store
import { Store } from "../../../store";
import { eventEmiterInstance } from "../../../utils/EventEmitter";
import { highlightLocations } from "../../../utils/helpers";

import { LoadingOutlined } from "@ant-design/icons";
import { LocationTypes } from "../../../utils/constants";
import LocationWiseProducts from "../../LocationWiseProducts/LocationWiseProducts";

const EMPTY_LOCATION_COLOR = "#f8821b"; // Orange color for empty bins
const FILLED_LOCATION_COLOR = "#0BDA51"; // Green color for filled bins

const Miscellaneous = ({ vref, isActive }) => {
  const { state, dispatch } = useContext(Store);
  const [loading, setLoading] = useState(false);
  const [selectedValue, setSelectedValue] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [locationWiseProducts, setLocationWiseProducts] = useState(null);

  const onSearchHandler = async (valueType) => {
    setLoading(true);
    try {
      const [productResponse, locationsResponse] = await Promise.all([axios.get(`inventory/Products`), axios.get(`warehouse-layout/bins`)]);

      //   1. Get current layout locations (by code)
      const currentActiveLayoutLocations = state?.elements
        ?.map((element) => {
          if (element?.objectType === "area") {
            if (element.subAreas?.length === 0 && element.code) {
              return element.code; // Include area code if no subAreas
            }
            return element.subAreas?.map((subArea) => subArea.code);
          }
          if (element?.objectType === "rack") {
            if (element.bins?.length === 0 && element.code) {
              return element.code; // Include rack code if no bins
            }
            return element.bins?.map((bin) => bin.code);
          }
          return false;
        })
        .filter(Boolean)
        .flat();

      //   2. Filter warehouse locations that exist in the current layout
      const warehouseStorageLocations = locationsResponse?.data?.data?.filter((loc) => currentActiveLayoutLocations.includes(loc.doc_location)) || [];

      //   3. Filter inventory products to only include those in current layout locations
      const allInventoryProducts = productResponse?.data?.data || [];
      const inventoryProducts = allInventoryProducts.filter((product) => currentActiveLayoutLocations.includes(product.doc_location));

      if (inventoryProducts.length === 0 && allInventoryProducts.length > 0) {
        setInfoMessage("No inventory found in current warehouse layout");
      } else if (inventoryProducts.length === 0) {
        setInfoMessage("Warehouse Currently Holds No Inventory");
      } else if (infoMessage) {
        setInfoMessage(null);
      }

      //   4. Determine filled & empty locations
      const filledProducts = inventoryProducts.filter((product) => Number(product.quantity) > 0);

      const filledLocations = filledProducts.map((p) => p.doc_location);
      const emptyStorageLocations = warehouseStorageLocations.filter((loc) => !filledLocations.includes(loc.doc_location));

      const emptyStorageLocationCodes = emptyStorageLocations.map((loc) => loc.doc_location);

      const isFilledLocations = valueType === LocationTypes.FILLED;
      const storageLocationCodes = isFilledLocations ? filledLocations : emptyStorageLocationCodes;

      //   5. Build groupedProducts dynamically based on valueType
      let groupedProducts = {};

      if (isFilledLocations) {
        // --- FILLED VIEW ---
        filledProducts.forEach((product) => {
          const { doc_location, product_name, quantity } = product;
          const normalizedName = product_name?.trim()?.toLowerCase() || "unknown";

          if (!groupedProducts[doc_location]) groupedProducts[doc_location] = [];

          const existing = groupedProducts[doc_location].find((p) => p.product_name.toLowerCase() === normalizedName);

          if (existing) {
            existing.quantity += Number(quantity);
          } else {
            groupedProducts[doc_location].push({
              product_name: product_name.trim(),
              quantity: Number(quantity),
            });
          }
        });
      } else {
        // --- EMPTY VIEW ---
        emptyStorageLocationCodes.forEach((locCode) => {
          groupedProducts[locCode] = [];
        });
      }

      setLocationWiseProducts(groupedProducts);
      dispatch({ type: "SET_FILTER_ELEMENTS", payload: storageLocationCodes });

      return {
        valueType,
        storageLocationCodes,
        filteredProducts: groupedProducts,
      };
    } catch (error) {
      console.log("error", error);
    } finally {
      setLoading(false);
    }
  };

  const onShowHandler = (type, storageLocationCodes, filteredProducts) => {
    // Highlight locations in the 3D view
    const uniqueLocations = [...new Set(storageLocationCodes)];

    if (type === LocationTypes.FILLED) {
      highlightLocations(uniqueLocations, FILLED_LOCATION_COLOR);
    } else if (type === LocationTypes.EMPTY) {
      highlightLocations(uniqueLocations, EMPTY_LOCATION_COLOR);
    } else if (type === LocationTypes.ALL) {
      eventEmiterInstance.emit("resetAllRacks", {});
    }
  };

  const onChangeHandler = (value_type) => {
    setLocationWiseProducts(null);
    if (value_type !== LocationTypes.ALL) {
      onSearchHandler(value_type)
        .then(({ valueType, storageLocationCodes, filteredProducts }) => {
          onShowHandler(valueType, storageLocationCodes, filteredProducts);
        })
        .catch((error) => {
          console.log("error", error);
        });
    } else {
      onShowHandler(value_type, [], []);
    }

    setSelectedValue(value_type);
  };

  useEffect(() => {
    if (!isActive) {
      setSelectedValue(null);
    } else {
      //fetchOwnerShipLookup();  //Uncomment this line if you want to fetch ownerShipLookup
    }
  }, [isActive]);

  return (
    <div className="miscellaneous-container">
      <div className="miscellaneous-wrapper">
        <div className="empty-space-wrapper">
          {/* <h1 className="empty-space-title">Empty Space</h1> */}
          <div className="empty-space-content">
            <Radio.Group value={selectedValue} onChange={(e) => onChangeHandler(e.target.value)}>
              <Radio value={LocationTypes.FILLED}>
                Filled Locations <span className="circle filled"></span>
              </Radio>
              <Radio value={LocationTypes.EMPTY}>
                Empty Locations <span className="circle empty"></span>
              </Radio>
              <Radio value={LocationTypes.ALL}>All</Radio>
            </Radio.Group>
          </div>
        </div>

        {infoMessage && <div style={{ textAlign: "center", color: "red" }}>{infoMessage}</div>}

        {loading && (
          <div style={{ textAlign: "center" }}>
            Loading inventory <LoadingOutlined theme="outlined" className="category-loader" style={{ color: "#576ec1", fontSize: "10px", strokeWidth: "20" }} />
          </div>
        )}

        {locationWiseProducts ? <LocationWiseProducts locationProducts={locationWiseProducts} /> : null}
      </div>
    </div>
  );
};

export default Miscellaneous;
